import { fireTag } from '@vue-util/tagManager';
import CacheManager from '@vue-util/cacheManager';
import constants from '../utils/constants';

import {
  isMcom,
  getCountryCode,
  getCustomerId,
  getStateCode,
  getVisitorId,
  getZipCode,
} from '../utils/utils';
import {
  getAAPageType,
  getBatchId,
  getCGIDParameter,
  getHeaderId,
} from './analyticsHelper';
import { track } from './trackingPixel';
import prosCollectionComponentType from '../utils/prosCollectionComponentType';

const { ANALYTICS, HNF_ZONE } = constants;

export const prosFbtEventTag = ({
  rtoData, zone, items, event,
}) => {
  const choiceId = encodeURIComponent(rtoData?.choiceId?.split('-')[0]);
  const milanid = rtoData?.treatmentId ? `milanid:${rtoData?.treatmentId}` : 'milanid:blank';
  const expData = items.map((item, i) => ({
    method_type: 'P',
    link_element: item.detail?.name || '',
    content_position: `r99s0${items.length > 1 ? i + 1 : 2}`,
    zone_type: ANALYTICS.PROS,
    zone_title: ANALYTICS.FBT,
    media_type: 'FBT PDP',
    attribute1: `PROSFBT:${choiceId}:${zone}`,
    attribute2: rtoData.productId || '',
    attribute3: milanid,
  }));

  const productModel = items.length > 1 ? items[1] : items[0];
  const uTagData = {
    ...productModel?.metadata,
    event_name: event,
    impression_data: expData,
    impression_type: ANALYTICS.FBT,
  };

  fireTag('link', uTagData, { immediate: true });
};

const buildImpressionData = (rtoData, zone, impressionData) => {
  let eventName = ANALYTICS.CONTENT_VIEW;
  const milanid = rtoData?.treatmentId && `milanid:${rtoData?.treatmentId}`;
  const choiceId = rtoData?.choiceId && encodeURIComponent(rtoData.choiceId.split('-')[0]);
  if (isMcom()) {
    impressionData.attribute2 = milanid;
  } else {
    eventName = 'recommendation impression';
    impressionData.attribute3 = milanid;
    impressionData.media_type = getAAPageType('bcom', zone);
  }
  const impressionTag = {
    method_type: 'P',
    zone_type: ANALYTICS.PROS,
    zone_title: rtoData?.content?.contentHeader || rtoData?.headerText || '',
    attribute1: `PROS:${choiceId || ''}:${zone}`,
    link_element: rtoData?.content?.contentPath?.split('/')[5] || '',
    ...impressionData,
  };
  return {
    event_name: eventName,
    impression_data: [impressionTag],
    pros_zone_impression: `${zone}|${choiceId || 'no model'}|${impressionTag.zone_title}`,
  };
};
const handleMcomEvent = (rtoData, zone, impressionData, properties) => {
  const shouldFireEvent = properties?.prosImpressionTaggingEnabled && rtoData?.impTag
      && impressionData?.zone_type !== ANALYTICS.RVI;
  const productType = (!rtoData?.content && rtoData.productType && rtoData.productType?.toString() !== '-9')
    ? `_${rtoData.productType}` : '';
  const sessionExists = CacheManager.getSession(zone + productType);

  if (shouldFireEvent && !sessionExists) {
    try {
      fireTag('link', buildImpressionData(rtoData, zone, impressionData));
      if (productType) {
        CacheManager.setSession(zone + productType, rtoData.choiceId.split('-')[0], 30);
      } else {
        CacheManager.setSession(zone, rtoData.choiceId.split('-')[0]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`contentViewEventTag fail ${e}`);
    }
  }
};
export const contentViewEventTag = (rtoData, zone, impressionData, properties) => {
  if (isMcom()) {
    setTimeout(() => {
      handleMcomEvent(rtoData, zone, impressionData, properties);
    }, 1000);
  } else {
    try {
      fireTag('link', buildImpressionData(rtoData, zone, impressionData));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`contentViewEventTag fail ${e}`);
    }
  }
};

export const linkClickEventTag = (productId, position, rtoData, zone, impressionData = {}) => {
  try {
    let recommendationLinkClickTagData;

    if (zone === HNF_ZONE) {
      recommendationLinkClickTagData = {
        event_name: 'recommendation link click',
        recommendation_milanid: 'no milanid',
        recommendation_model: 'no model',
        recommendation_position: position?.toString() ?? '',
        recommendation_title: impressionData.zone_title ?? (rtoData?.headerText || 'RECENTLY VIEWED ITEMS'),
        recommendation_zone: 'RVI_SSUGG_ZONE',
      };
    } else {
      const choiceId = encodeURIComponent(rtoData?.choiceId?.split('-')?.[0] ?? '');
      const model = choiceId?.substring(3, 6) ?? '';

      recommendationLinkClickTagData = {
        event_name: 'recommendation link click',
        recommendation_model: model,
        recommendation_title: impressionData.zone_title ?? rtoData?.headerText,
        recommendation_position: position?.toString() ?? '',
        recommendation_zone: zone,
        ...(rtoData?.treatmentId ? { recommendation_milanid: rtoData?.treatmentId } : {}),
        ...(!isMcom() && {
          recommendation_page_type: getAAPageType('bcom', zone),
        }),
      };
    }

    fireTag('link', recommendationLinkClickTagData);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`linkClickEventTag fail ${e}`);
  }
};

export const contentlinkClickEventTag = (rtoData, zone) => {
  try {
    const categoryId = rtoData.encodedChoiceId.split('@')[2].split('$')[1];
    if (categoryId) {
      const content = rtoData?.content;
      let mediaType = 'pros:EventZone';
      if (content) {
        const isCollection = content?.isCollection;
        if (isCollection) {
          mediaType = 'PROS:Collections';
        } else {
          mediaType = 'PROS:Content';
        }
      }
      const choiceId = encodeURIComponent(rtoData.choiceId.split('-')[0]);

      const impressionData = [];
      const uTagData = {
        method_type: 'P',
        zone_type: 'pros',
        link_element: content?.contentPath.split('/')[5] || rtoData.headerText,
        zone_title: content?.contentHeader || rtoData.headerText,
        content_position: 'r99s99',
        media_type: mediaType,
        attribute2: rtoData?.treatmentId ? `milanid:${rtoData?.treatmentId}` : 'milanid:blank',
        attribute1: `PROS:${choiceId}:${zone}`,
      };
      impressionData.push(uTagData);

      fireTag('link', {
        event_name: 'content link click',
        impression_data: impressionData,
        pros_zone_impression: `${zone}|${choiceId}`,
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`contentlinkClickEventTag fail ${e}`);
  }
};

export const firePixelOrScrollEvent = ({
  name,
  requester,
  zone,
  pList,
  recProductIds,
  encodedChoiceId,
  contentId,
}) => {
  if (!pList || !zone || !requester || !(encodedChoiceId || zone.toLowerCase().includes('rvi'))) return;

  try {
    const rProductIds = recProductIds.join('|');
    const countryCode = getCountryCode();
    const zipCode = getZipCode();
    const stateCode = getStateCode();
    const visitorId = getVisitorId();
    const cgId = getCGIDParameter(null, encodedChoiceId);
    const dId = getBatchId(null, encodedChoiceId);
    const hId = getHeaderId(null, encodedChoiceId);
    const cId = getCustomerId();

    const queryParams = new URLSearchParams({
      pList,
      r: requester,
      c: zone,
      rType: name,
      ...(visitorId && { vId: visitorId }),
      ...(cId && { cId }),
      ...(cgId && { cgId }),
      ...(hId && { hId }),
      ...(dId && { dId }),
      ...(rProductIds && { rProductIds }),
      ...(stateCode && { stateCode }),
      ...(zipCode && { zipCode }),
      ...(countryCode && { countryCode }),
      ...(contentId && { contentId }),
    });

    const eventData = decodeURI(queryParams.toString());

    track(encodeURI(eventData), {
      pixelHost: window.location.origin,
      pathToPixel: '/EventsWar/events/pixel/customeraction',
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`firePixelOrScrollEvent fail ${e}`);
  }
};

export const fireImpressionLinkTagging = ({
  products,
  recommendedItems = [],
  type,
  zone,
  anchorProductId,
}) => {
  try {
    const { choiceId = '' } = recommendedItems[0] || {};
    const prosControlGroupId = choiceId.split('-')[0]?.substring(3, 9);

    const expData = products.map((product, index) => ({
      method_type: 'P',
      link_element: product.detail.name,
      content_position: `r99s0${index + 1}`,
      zone_type: ANALYTICS.PROS,
      zone_title: ANALYTICS.FBT,
      media_type: 'FBT PDP',
      attribute1: `PROSFBT:${prosControlGroupId}:${zone}`,
      attribute2: anchorProductId,
    }));

    const uTagData = {
      ...((products[products.length - 1]?.meta || {}).analytics || {}).data,
      event_name:
        type === 'presented' ? ANALYTICS.CONTENT_VIEW : 'content link click',
      impression_data: expData,
      impression_type: ANALYTICS.FBT,
    };

    fireTag('link', uTagData, { immediate: true });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`fireImpressionLinkTagging fail ${e}`);
  }
};

export const fireRVIEditClickTag = (zone = '', title = '') => {
  try {
    fireTag('link', {
      event_name: 'pros rvi edit click',
      site_action: 'rvi edit start',
      action_location: 'recently viewed edit',
      recommendation_model: '',
      recommendation_title: title,
      recommendation_zone: zone,
      ...(!isMcom() && {
        recommendation_page_type: prosCollectionComponentType.RVI_CAROUSEL,
      }),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`linkClickEventTag fail ${e}`);
  }
};

export const fireRVIEditDoneClickTag = (zone = '', title = '') => {
  try {
    fireTag('link', {
      event_name: 'pros rvi done click',
      custom_link_name: 'pros rvi done click',
      recommendation_model: '',
      recommendation_title: title,
      recommendation_zone: zone,
      ...(!isMcom() && {
        recommendation_page_type: prosCollectionComponentType.RVI_CAROUSEL,
      }),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`linkClickEventTag fail ${e}`);
  }
};

export const fireRVIEditRemoveClickTag = (productId, zone = '', title = '') => {
  try {
    fireTag('link', {
      event_name: 'pros rvi remove click',
      site_action: 'rvi edit complete',
      action_location: 'recently viewed edit',
      recommendation_model: '',
      product_id: productId,
      recommendation_title: title,
      // recommendation_position: position || '', add
      recommendation_zone: zone,
      ...(!isMcom() && {
        recommendation_page_type: prosCollectionComponentType.RVI_CAROUSEL,
      }),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`linkClickEventTag fail ${e}`);
  }
};

export const fireHeartIconClickedTag = (isInWishlist, position, productId, zone, title, rtoData) => {
  const choiceId = encodeURIComponent(rtoData?.choiceId?.split('-')?.[0] ?? '');
  const model = choiceId?.substring(3, 6) ?? '';
  try {
    fireTag('link', {
      event_name: `pros heart icon ${isInWishlist ? 'remove' : 'add' } click`,
      recommendation_model: model,
      recommendation_title: title || rtoData?.headerText,
      recommendation_position: position?.toString() ?? '',
      recommendation_zone: zone,
      ...(isInWishlist
        ? {
          event_name: 'pros wishlist remove',
          event_type: 'pros wishlist remove',
          custom_link_name: 'pros heart icon remove click',
        }
        : {
          event_name: 'pros wishlist add',
          event_type: 'pros wishlist add',
          custom_link_name: 'pros heart icon add click',
        }
      ),
      ...(rtoData?.treatmentId ? { recommendation_milanid: rtoData?.treatmentId } : {}),
      ...(!isMcom() && {
        recommendation_page_type: getAAPageType('bcom', zone),
      }),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`fireHeartIconClickedTag fail ${e}`);
  }
};

export const fireProductPoolAllClicked = (title = '') => {
  try {
    fireTag('link', {
      event_name: `hp product pool ${title}`,
      link_name: `hp product pool ${title}`,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`fireProductPoolAllClicked fail ${e}`);
  }
};

export const fireProductPoolChevronClicked = (position = '') => {
  const chevronPosition = `pool_${position}`;
  try {
    fireTag('link', {
      event_name: `hp product ${chevronPosition}`,
      link_name: `hp product ${chevronPosition}`,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`fireProductPoolChevronClicked(${chevronPosition}) fail ${e}`);
  }
};
